import React, { useEffect, useRef, useState } from 'react';

interface AIChatPromptProps {
  children: React.ReactNode;
  typingSpeed?: number; // ms per character
  className?: string;
  style?: React.CSSProperties;
  animate?: boolean; // If false, show all content immediately
}

// Helper to extract all text content from React children and count non-text elements
function extractText(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractText).join('');
  }
  if (React.isValidElement(node)) {
    const props = (node as any).props || {};
    const childText = extractText(props.children);
    
    // For elements that don't contain text (like SVG, img), add a placeholder character
    const elementTypes = ['svg', 'img', 'input', 'br', 'hr'];
    if (typeof node.type === 'string' && elementTypes.includes(node.type)) {
      return childText + ' '; // Add a space to represent the element
    }
    
    return childText;
  }
  return '';
}

const AIChatPrompt: React.FC<AIChatPromptProps> = ({ 
  children, 
  typingSpeed = 18, 
  className, 
  style, 
  animate = true 
}) => {
  const [visibleChars, setVisibleChars] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevChildrenRef = useRef<React.ReactNode>(null);
  
  const totalText = extractText(children);
  const totalChars = totalText.length;

  // Start animation when children change or animate prop changes
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!animate) {
      setVisibleChars(totalChars);
      return;
    }

    // Check if content actually changed
    if (children !== prevChildrenRef.current) {
      prevChildrenRef.current = children;
      
      // Reset and start typing animation
      setVisibleChars(0);
      
      if (totalChars === 0) return;

      let currentChar = 0;
      
      const typeNextChar = () => {
        currentChar++;
        setVisibleChars(currentChar);
        
        if (currentChar < totalChars) {
          timeoutRef.current = setTimeout(typeNextChar, typingSpeed);
        }
      };

      // Start typing after a small delay
      timeoutRef.current = setTimeout(typeNextChar, typingSpeed);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [children, animate, totalChars, typingSpeed]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Render children with typing effect
  const renderWithTyping = (node: React.ReactNode, charIndex: { current: number }): React.ReactNode => {
    if (typeof node === 'string' || typeof node === 'number') {
      const text = String(node);
      const startIndex = charIndex.current;
      const endIndex = startIndex + text.length;
      const shouldShow = startIndex < visibleChars;
      const partialLength = Math.max(0, Math.min(text.length, visibleChars - startIndex));
      
      charIndex.current = endIndex;
      
      return shouldShow ? text.slice(0, partialLength) : '';
    }

    if (Array.isArray(node)) {
      return node.map((child, index) => (
        <React.Fragment key={index}>
          {renderWithTyping(child, charIndex)}
        </React.Fragment>
      ));
    }

    if (React.isValidElement(node)) {
      const props = (node as any).props || {};
      const startIndex = charIndex.current;
      
      // Check if this is a block element
      const blockElements = ['div', 'button', 'img', 'input', 'textarea', 'select', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
      const isBlockElement = typeof node.type === 'string' && blockElements.includes(node.type);
      
      if (isBlockElement) {
        // For block elements, we still need to animate their text content
        // but treat them as containers that appear when we start typing their content
        const blockTextLength = extractText(props.children).length;
        const blockStarted = startIndex < visibleChars;
        
        if (!blockStarted) {
          // Haven't reached this block yet, skip it entirely
          charIndex.current = startIndex + blockTextLength;
          return null;
        }
        
        // Block has started, render it with animated children
        const renderedChildren = renderWithTyping(props.children, charIndex);
        
        return React.cloneElement(node, {
          ...props,
          children: renderedChildren,
          style: {
            ...props.style,
            opacity: 1,
          }
        });
      } else {
        // For inline elements, check if we've reached their position
        const elementTextLength = extractText(props.children).length;
        // If element has no text content (like SVG, img), treat it as 1 character
        const effectiveLength = elementTextLength > 0 ? elementTextLength : 1;
        const elementStarted = startIndex < visibleChars;
        
        if (!elementStarted) {
          // Haven't reached this inline element yet, skip it
          charIndex.current = startIndex + effectiveLength;
          return null;
        }
        
        // Element has started, render it with animated children (if any)
        const renderedChildren = renderWithTyping(props.children, charIndex);
        
        return React.cloneElement(node, {
          ...props,
          children: renderedChildren,
        });
      }
    }

    return null;
  };

  if (!animate) {
    return (
      <div className={className} style={{ whiteSpace: 'pre-wrap', ...style }}>
        {children}
      </div>
    );
  }

  return (
    <div className={className} style={{ whiteSpace: 'pre-wrap', ...style }}>
      {renderWithTyping(children, { current: 0 })}
    </div>
  );
};

export default AIChatPrompt;
